import { useCallback, useEffect, useMemo, useState } from "react";

import useConfirmNavigation from "psims/react/util/use-confirm-navigation";
import { ViewMode } from "../../shared/use-view-mode";
import useDataSubmissionSaveCheck from "../../shared/use-data-submission-save-check";
import { attemptScrollToSelector } from "psims/react/pages/primary-pages/data-submissions/shared/view-utils";
import { UseSubmit } from "../../shared/use-submit";
import { SELECTOR_NOTIFICATIONS } from "psims/constants/selectors";
import { UseRefinerAnnualActivityAPI } from "./use-refiner-annual-activity-api";
import { UseRefinerAnnualActivityProgress } from "./use-refiner-annual-activity-progress";
import { any } from "psims/lib/collections";
import { UseStorageSites } from "../shared/storage-sites/use-storage-sites";
import { UseRefinerAnnualActivities } from "./annual-activities/use-refiner-annual-activities";
import { UseMsoComment } from "../shared/mso-comment/use-mso-comment";
import { isEmpty } from "psims/lib/empty";
import { UseMsoStockholdingStorages } from "../shared/mso-stockholding-storages/use-mso-stockholding-storages";
import { UseImporterImports } from "../shared/imports/use-importer-imports";
import useEnforceableArrangements from "../shared/enforceable-arrangements/use-enforceable-arrangements";
import useAustralianControllingCorporations from "../shared/australian-controlling-corporations/use-australian-controlling-corporations";
import { UseRefinerProduction } from "../shared/production/use-refiner-production"; //'./production/use-refiner-production';
import { UpdateMsoCommentAnnualActivityVM } from "psims/gen/xapi-client/models/UpdateMsoCommentAnnualActivityVM";


/**
 * Controller to manage in-form and browser navigation, e.g. unsaved
 * change checking and validation alert dialogs where necessary.
 */
interface UseRefinerAnnualActivityNavigationProps {
    annualActivitiesCtrl: UseRefinerAnnualActivities;
    apiCtrl: UseRefinerAnnualActivityAPI;
    commentsCtrl: UseMsoComment;
    importsCtrl: UseImporterImports;
    pageChanges: Array<Array<boolean>>;
    progressCtrl: UseRefinerAnnualActivityProgress;
    submitCtrl: UseSubmit;
    msoStockholdingStorageCtrl: UseMsoStockholdingStorages;
    enforceableArrangementsCtrl: ReturnType<typeof useEnforceableArrangements>;
    australianControllingCorporationsCtrl: ReturnType<typeof useAustralianControllingCorporations>;
    productionCtrl: UseRefinerProduction; 
    viewMode: ViewMode;
    msoCommentAnnualActivities: UpdateMsoCommentAnnualActivityVM[]; 
}

function useRefinerAnnualActivityNavigation({
    annualActivitiesCtrl,
    apiCtrl,
    commentsCtrl,
    importsCtrl,
    pageChanges,
    progressCtrl,
    msoStockholdingStorageCtrl,
    enforceableArrangementsCtrl,
    australianControllingCorporationsCtrl,
    productionCtrl,
    submitCtrl,
    viewMode,
    msoCommentAnnualActivities
}: UseRefinerAnnualActivityNavigationProps) {
    const hasUnsavedChanges = useMemo(() => {
        const changes = pageChanges[progressCtrl.currentStep.index];
        if (changes != null) {
            return any(changes, c => c);
        }
        return false;
    }, [pageChanges, progressCtrl.currentStep.index]);

    const saveCheckCtrl = useDataSubmissionSaveCheck({
        apiCtrl,
        hasUnsavedChanges,
        updateDataSubmission: submitCtrl.dataSubmissionUpdate,
        updateSubmission: {
            dataSubmissionId: apiCtrl.submission?.dataSubmission.id,
            annualActivities: annualActivitiesCtrl.requestEntities,
            productions: productionCtrl.requestEntities,
            imports: importsCtrl.requestEntities,
            msoComment: commentsCtrl.requestPayload,
            msoStockholdingStorages: msoStockholdingStorageCtrl.requestEntities,
            enforceableArrangements: enforceableArrangementsCtrl.requestEntities,
            australianControllingCorporations: australianControllingCorporationsCtrl.requestEntities,
            msoCommentAnnualActivities: msoCommentAnnualActivities,
        }
    });

    const browserNavCtrl = useConfirmNavigation({ when: hasUnsavedChanges && viewMode === 'edit' });
    const [lastSavedPageIndex, setLastSavedPageIndex] = useState<number | null>(null);
    const [lastSaveAttemptIndex, setLastSaveAttemptIndex] = useState<number | null>(null);

    const isPageValid = useMemo(() => {
    switch (progressCtrl.currentStep.index) {
        case 0: {  // Imports + Annual Activities
            return [
                ...(annualActivitiesCtrl.updateAnnualActivities ?? []).filter(aa => !isEmpty(aa.validations)),
                ...(importsCtrl.updateImports ?? []).filter(imp => !isEmpty(imp.validations)),
                ...(isEmpty(commentsCtrl.validations) ? [] : [commentsCtrl]),
            ].length === 0;
        }
        case 1:    // Production — no validation yet
            return true;
        case 2: {  // Storage (was 1)
            return (msoStockholdingStorageCtrl.updateStorages ?? [])
                .filter(ss => !isEmpty(ss.validations)).length === 0;
        }
        case 3: {  // Enforceable arrangements (was 2)
            return (enforceableArrangementsCtrl.rows ?? [])
                .filter(r => !isEmpty(r.validations)).length === 0;
        }
        case 4:    // Australian Controlling Corporations (was 3)
            return !australianControllingCorporationsCtrl.anyInvalid;
        default: return true;
    }
}, [progressCtrl.currentStep.index, annualActivitiesCtrl, importsCtrl,
    msoStockholdingStorageCtrl, enforceableArrangementsCtrl,
    australianControllingCorporationsCtrl, commentsCtrl, submitCtrl]);

    useEffect(() => {
        if (browserNavCtrl.showPrompt && saveCheckCtrl.saveState === 'idle') {
            saveCheckCtrl.checkAndConfirm(
                progressCtrl.currentStep.kind === 'data' ? 'submission' : 'data_submission',
                () => browserNavCtrl.handleDecision(true),
                () => browserNavCtrl.handleDecision(false)
            );
        }
    }, [browserNavCtrl, progressCtrl, saveCheckCtrl]);

    const unsavedChangesDialogCtrl = useMemo(() => ({
        controlsState: (saveCheckCtrl.saveState === 'saving' ? 'loading' : 'normal') as 'loading' | 'normal',
        isOpen: saveCheckCtrl.saveState === 'confirming',
        onCancel: saveCheckCtrl.cancel,
        onConfirm: () => saveCheckCtrl.save(),
        onSecondary: saveCheckCtrl.proceed,
    }), [saveCheckCtrl]);

    const onSave = useCallback((index: number) => {
        setLastSavedPageIndex(index);
        progressCtrl.goToStep(index);
        setTimeout(() => {
            document.getElementById('page')?.scrollTo({ top: 300, behavior: 'smooth' });
        }, 100);
    }, [progressCtrl]);

    const ensureValid = useCallback(() => {
        if (!isPageValid) {
            attemptScrollToSelector(SELECTOR_NOTIFICATIONS);
        }
        return isPageValid;
    }, [isPageValid]);

    const attemptStepNavigation = useCallback((index: number) => {
        if (viewMode !== 'edit') {
            progressCtrl.goToStep(index);
            return;
        }
        saveCheckCtrl.checkAndConfirm(
            progressCtrl.currentStep.kind === 'data' ? 'submission' : 'data_submission',
            () => onSave(index),
            () => {}
        );
    }, [ensureValid, onSave, progressCtrl, saveCheckCtrl, viewMode]);

    const saveAndGoToPage = useCallback((index: number) => {
        setLastSaveAttemptIndex(progressCtrl.currentStep.index);
        if (!ensureValid()) {
            return;
        }
        saveCheckCtrl.save(
            progressCtrl.currentStep.kind === 'data' ? 'submission' : 'data_submission',
            () => onSave(index)
        );
    }, [ensureValid, onSave, progressCtrl, saveCheckCtrl]);

    const attemptSubmit = useCallback(() => {
        submitCtrl.attemptSubmit();
    }, [submitCtrl]);

    return {
        lastSaveAttemptIndex,
        lastSavedPageIndex,
        unsavedChangesDialogCtrl,
        attemptStepNavigation,
        attemptSubmit,
        saveAndGoToPage,
    };
}

export default useRefinerAnnualActivityNavigation

export type UseRefinerAnnualActivityNavigation = ReturnType<typeof useRefinerAnnualActivityNavigation>;
